import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Database,
  Server,
  Shield,
  GraduationCap,
  Hospital,
  Factory,
  ShoppingCart,
  Users,
  CheckCircle2,
  ArrowRight,
  Clock,
  HeadphonesIcon
} from 'lucide-react';

const ERPAMCSolutions = () => {
  const erpSolutions = [
    {
      icon: GraduationCap,
      title: "School Management ERP",
      features: ["Student Management", "Fee Management", "Attendance Tracking", "Timetable Management", "Examination System", "Parent Portal"]
    },
    {
      icon: Hospital,
      title: "Hospital Management ERP",
      features: ["Patient Records", "Appointment Scheduling", "Billing System", "Pharmacy Management", "Lab Management", "Doctor Portal"]
    },
    {
      icon: Factory,
      title: "Manufacturing ERP",
      features: ["Inventory Control", "Production Planning", "Quality Control", "Supply Chain", "Warehouse Management", "Reporting"]
    },
    {
      icon: ShoppingCart,
      title: "Retail & POS",
      features: ["Billing System", "Inventory Management", "Customer Management", "Sales Analytics", "Multi-store Support", "Loyalty Program"]
    }
  ];

  const crmFeatures = [
    "Sales CRM & Pipeline Management",
    "Customer Support CRM",
    "Lead Tracking & Scoring",
    "Marketing Automation",
    "Email Integration",
    "Custom Reporting & Analytics"
  ];

  const amcServices = [
    { name: "Website Maintenance", desc: "Regular updates and content management" },
    { name: "Software Updates & Support", desc: "Bug fixes and feature enhancements" },
    { name: "IT Infrastructure Support", desc: "Server and network maintenance" },
    { name: "Security Updates & Patches", desc: "Regular security monitoring" },
    { name: "Backup Monitoring", desc: "Data backup and recovery services" },
    { name: "24/7 Technical Support", desc: "Round-the-clock assistance" },
    { name: "Performance Optimization", desc: "Speed and efficiency improvements" },
    { name: "Cloud Hosting Support", desc: "Cloud infrastructure management" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-4">ERP / AMC Solutions</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Complete{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              ERP & Business Solutions
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Streamline your operations with industry-specific ERP systems, CRM solutions, 
            and comprehensive AMC services for seamless business management.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
              Book a Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ERP Solutions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">ERP Solutions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Industry-specific enterprise resource planning systems tailored to your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {erpSolutions.map((solution, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                    <solution.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{solution.title}</h3>
                </div>
                <ul className="space-y-3">
                  {solution.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          {/* Additional ERP Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <Database className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">HRMS & Payroll</h3>
              <p className="text-gray-600">Complete employee management and payroll system</p>
            </Card>
            <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Accounting Systems</h3>
              <p className="text-gray-600">Financial management and accounting solutions</p>
            </Card>
            <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <Clock className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Attendance Systems</h3>
              <p className="text-gray-600">Biometric and digital attendance tracking</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CRM Solutions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                CRM Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Manage customer relationships effectively with our comprehensive CRM systems 
                designed to boost sales and improve customer satisfaction.
              </p>
              <div className="space-y-4">
                {crmFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/8373815/pexels-photo-8373815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="CRM Solutions"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AMC Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              AMC (Annual Maintenance Contract)
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive maintenance and support services for your IT infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {amcServices.map((service, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Server className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{service.name}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </Card>
            ))}
          </div>

          {/* Additional Services */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="p-8 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <Shield className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cyber Security</h3>
              <p className="text-gray-600">Basic security services and monitoring</p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <Database className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cloud Hosting</h3>
              <p className="text-gray-600">Reliable cloud hosting and migration support</p>
            </Card>
            <Card className="p-8 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100">
              <HeadphonesIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">IT Consulting</h3>
              <p className="text-gray-600">Expert guidance for technology decisions</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Streamline Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a personalized demo of our ERP and CRM solutions
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg">
              Schedule Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ERPAMCSolutions;
